import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import { useState } from 'react';

import { useFormikContext } from 'formik';
import { Firm, Office } from 'types/firm';
import { FormAction } from 'types/firmInfo';

import { makeStyles } from '@bequestinc/wui';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';

import { FirmsQueryKey } from 'hooks/query-hooks/firms/useFirmsQuery';
import { useChangePrimaryOfficeMutation } from 'hooks/query-hooks/offices/useOfficeMutation';
import { useCurrentUserQuery } from 'hooks/query-hooks/users/useUserQuery';

import { ADD, OFFICE_APPROVED, UPDATE } from 'utils/constants';
import { isPhysicalOffice } from 'utils/office';

import DeleteOfficeInfoModal from 'components/pages/firm-info/DeleteOfficeInfoModal';

const useStyles = makeStyles()({
  button: {
    width: 100,
  },
  largeButton: {
    width: 200,
  },
});

interface PropTypes {
  enabled: boolean;
  action: FormAction;
  // physical or virtual office, only fields that exist on the Office type are used
  office: Partial<Office>;
}

const OfficeFormButtons = ({ enabled, action, office }: PropTypes) => {
  // Formik's own isSubmitting flips to true synchronously on click, before validation and the
  // mutation kick off, closing the window where a double click could fire two submissions.
  const { errors, isSubmitting } = useFormikContext();
  const { classes } = useStyles();
  const { data: user } = useCurrentUserQuery();
  const [showDeleteOfficeInfoModal, setShowDeleteOfficeInfoModal] = useState(false);
  const queryClient = useQueryClient();

  const isPhysical = isPhysicalOffice(office);

  const buttonText = () => {
    if (action === ADD || action === UPDATE) {
      return action;
    }
    return 'Submit';
  };

  const handleUpdateOfficeQueryData = (oldData: Firm): Office[] => {
    // If user is panel rep, make primary office change reflect in cache. If user is not a panel rep, a firm update is
    // created in the backend instead of implementing the primary office change, so instead set hasPrimaryOfficeUnderReview to true in cache
    if (user.isPanelRepUser) {
      return oldData.offices.map(oldOffice =>
        oldOffice.id === office.id
          ? { ...(office as Office), isPrimaryOffice: true }
          : { ...oldOffice, isPrimaryOffice: false },
      );
    }
    return oldData.offices.map(oldOffice => ({ ...oldOffice, hasPrimaryOfficeUnderReview: true }));
  };

  const changePrimaryOfficeMutation = useChangePrimaryOfficeMutation({
    onSuccess: () => {
      queryClient.setQueryData<Firm>([...FirmsQueryKey, office.firm], oldData => ({
        ...oldData,
        offices: handleUpdateOfficeQueryData(oldData),
      }));
    },
  });

  const changePrimaryOffice = () => {
    changePrimaryOfficeMutation.mutateAsync({ firm: office.firm, id: office.id });
  };

  return (
    <Grid container item direction="row" justifyContent="space-between">
      <Grid
        item
        container
        direction="row"
        justifyContent="flex-start"
        spacing={7}
        xs={10}
        alignItems="flex-start"
        wrap="nowrap"
      >
        {enabled && (
          <Grid item>
            <LoadingButton
              type="submit"
              loading={isSubmitting}
              disabled={Object.keys(errors).length > 0 || isSubmitting}
              variant="contained"
              color="primary"
              className={classes.button}
            >
              {buttonText()}
            </LoadingButton>
          </Grid>
        )}

        {action === UPDATE && !office.isPrimaryOffice && enabled && isPhysical && (
          <Grid item>
            <Button
              variant="contained"
              disabled={office.fsmState !== OFFICE_APPROVED || office.hasPrimaryOfficeUnderReview}
              className={classes.largeButton}
              onClick={changePrimaryOffice}
            >
              Make Primary Office
            </Button>
            {office.hasPrimaryOfficeUnderReview && (
              <FormHelperText error sx={{ width: 225 }}>
                Current primary office change is under review for this firm.
              </FormHelperText>
            )}
          </Grid>
        )}
      </Grid>

      {enabled && action === UPDATE && (
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            sx={{ maxHeight: '40px', width: 100 }}
            disabled={office.isPrimaryOffice}
            onClick={() => setShowDeleteOfficeInfoModal(true)}
          >
            Remove
          </Button>
          {office.isPrimaryOffice && (
            <FormHelperText error sx={{ width: 100 }}>
              Can't remove a primary office!
            </FormHelperText>
          )}
        </Grid>
      )}

      {action === UPDATE && (
        <DeleteOfficeInfoModal
          officeId={office.id}
          isPhysicalOffice={isPhysical}
          open={showDeleteOfficeInfoModal}
          onClose={() => setShowDeleteOfficeInfoModal(false)}
          firmId={office.firm}
        />
      )}
    </Grid>
  );
};

export default observer(OfficeFormButtons);
